import { useToast } from '@chakra-ui/react';
import { useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { NotifyCohortDto } from '../../../models/user-cohort/dto/user-cohort.dto';
import { useNotifyCohort } from '../hooks/useNotifyCohort';

type Props = {
  cohortId: string;
  isOpen: boolean;
  onClose: () => void;
};

const initialFormData: NotifyCohortDto = {
  title: '',
  body: '',
  url: '',
};

export default function SendCohortNotificationModal({
  cohortId,
  isOpen,
  onClose,
}: Props) {
  const [formData, setFormData] = useState<NotifyCohortDto>({
    ...initialFormData,
  });
  const { mutateAsync: notify, isLoading } = useNotifyCohort(cohortId);
  const toast = useToast();

  const handleInputChange = (key: keyof NotifyCohortDto, value: string) => {
    setFormData({ ...formData, [key]: value });
  };

  const onModalClose = () => {
    setFormData({ ...initialFormData });
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const result = await notify({
        title: formData.title,
        body: formData.body,
        url: formData.url || undefined,
      });

      toast({
        title: translate('cohort.notification_sent_successfully'),
        description: `${result.sent} ${translate('cohort.sent')}, ${result.failed} ${translate('cohort.failed')}`,
        status: result.failed > 0 ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
      });

      onModalClose();
    } catch (error) {
      toast({
        title: translate('cohort.send_notification_failed'),
        description: error instanceof Error ? error.message : undefined,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const isTitleValid = formData.title.trim().length > 0;
  const isBodyValid = formData.body.trim().length > 0;
  const isFormValid = isTitleValid && isBodyValid;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onModalClose}
      title={translate('cohort.send_notification_to_cohort')}
      onSubmit={handleSubmit}
      submitLabel={translate('cohort.send')}
      cancelLabel={translate('common.cancel')}
      isSubmitting={isLoading}
      isSubmitDisabled={!isFormValid}
      size="md"
    >
      <FormStack>
        <FormField
          type="text"
          label={translate('cohort.notification_title')}
          value={formData.title}
          onChange={(value) => handleInputChange('title', value)}
          placeholder={translate('cohort.notification_title_placeholder')}
          error={!isTitleValid && formData.title.length > 0 ? translate('cohort.title_required') : undefined}
          isRequired
        />

        <FormField
          type="textarea"
          label={translate('cohort.notification_body')}
          value={formData.body}
          onChange={(value) => handleInputChange('body', value)}
          placeholder={translate('cohort.notification_body_placeholder')}
          error={!isBodyValid && formData.body.length > 0 ? translate('cohort.body_required') : undefined}
          textareaProps={{ rows: 6 }}
        />

        <FormField
          type="text"
          label={translate('cohort.notification_url_optional')}
          value={formData.url}
          onChange={(value) => handleInputChange('url', value)}
          placeholder={translate('cohort.notification_url_placeholder')}
        />
      </FormStack>
    </FormModal>
  );
}
